import Table from "components/DataTable/Table";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import AttAddUpdateRecord from "./AttAddUpdateRecord";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";

export default function AttDailyLogs({
  parsedRecordTime,
  parsedRecordDate,
  getLogsByDate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  userAttendanceLogs,
  setUserAttendanceLogs,
  userData,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  // The same userId / deviceId the parent page loads these logs with.
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };
  // Delete Record
  const deleteRecord = async (id) => {
    try {
      loadingOn();
      const { data } = await API.delete("/attendance-logs/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        getLogsByDate();
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /**
   * Remove duplicate punches in the chosen range: for each day with more than
   * two, keep the earliest and latest (the in and out) and drop the rest.
   *
   * The server EXCLUDES them rather than deleting. A deleted device punch is
   * exactly what the next sync puts back, so deleting would undo itself.
   * Not offered for night-office users -- their shift crosses midnight, so
   * the earliest and latest punch of a calendar day are not their in and out.
   */
  const removeDuplicates = async () => {
    const from = startDate;
    const to = endDate || startDate;
    if (
      !window.confirm(
        `Remove duplicate punches for ${userData?.name || "this employee"} from ${from} to ${to}?\n\n` +
          "On each day with more than two punches, only the first (in) and last (out) are kept.",
      )
    ) {
      return;
    }
    try {
      loadingOn();
      const { data } = await API.post(
        "/attendance-logs/remove-duplicates",
        {
          userId: params.userId,
          deviceId: params.deviceId,
          startDate: from,
          endDate: to,
        },
        { headers: authHeader() },
      );
      dispatch(
        setNotification({
          message: data?.message,
          type: data?.success ? "success" : "error",
        }),
      );
      if (data?.success) getLogsByDate();
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      loadingOff();
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    getLogsByDate();
  }, []);

  /*   Table functions
   ********************************************* */
  const btn1 = () => (
    <div className="flex-grow-1 d-flex align-items-center">
      {/* Date Filter Section - Moved outside dropdown */}
      <div className="d-flex align-items-center ms-2">
        <div className="mx-2">From</div>
        <input
          className="form-control"
          value={startDate || ""}
          onChange={(e) => setStartDate(e.target.value)}
          type={"date"}
          style={{ width: "160px" }}
        />

        <div className="mx-2">To</div>
        <input
          className="form-control"
          value={endDate || ""}
          onChange={(e) => setEndDate(e.target.value)}
          type={"date"}
          style={{ width: "160px" }}
        />

        <button
          onClick={() => getLogsByDate()}
          className="btn btn-success mx-2"
        >
          FILTER
        </button>
        <button
          className="btn btn-primary "
          onClick={() => {
            setShowAddUpdateModal(true);
          }}
        >
          Add
        </button>
        <button
          className="btn btn-outline-danger ms-2"
          onClick={removeDuplicates}
          disabled={loading || !startDate || !!userData?.isNightOffice}
          title={
            userData?.isNightOffice ?
              "Not available for night-office users: their shift crosses midnight"
            : "Keep each day's first (in) and last (out) punch, remove the rest"
          }
        >
          Remove Duplicates
        </button>
      </div>
    </div>
  );

  const table_head = ["ID", "Attendance Time", "Attendance Date", "Actions"];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item?.id}</td>
      <td className="align-middle py-1">{parsedRecordTime(item.recordTime)}</td>
      <td className="align-middle py-1">{parsedRecordDate(item.recordTime)}</td>
      <td className="align-middle py-1">
        <button
          onClick={(e) => {
            setIsUpdateMode(true);
            setUpdateData(item);
            setShowAddUpdateModal(true);
          }}
          className="btn btn-sm btn-primary me-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>
        <DeleteModalButton
          deleteFunc={deleteRecord}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );
  return (
    <div>
      {/* <h2>Daily Attendance Logs</h2> */}
      {/*   Table
       ********************************************* */}
      <div className="px-2 flex-grow-1 d-flex">
        <div
          className="d-flex flex-grow-1"
          style={{ height: "85vh", overflowY: "auto" }}
        >
          <Table
            btn1={btn1}
            // defaultLimit={5}
            disableSearch={true}
            table_head={table_head}
            table_body={table_body}
            table_data={userAttendanceLogs}
          />
        </div>
      </div>
      {/* Modals */}
      <Modal
        contentClassName="bg-transparent"
        show={showAddUpdateModal}
        onHide={() => {
          setShowAddUpdateModal(false);
        }}
      >
        <Modal.Body className="bg-white custom-border-radius px-4">
          <AttAddUpdateRecord
            userAttendanceLogs={userAttendanceLogs}
            isUpdateMode={isUpdateMode}
            updateData={updateData}
            showModal={setShowAddUpdateModal}
            setIsUpdateMode={setIsUpdateMode}
            setUpdateData={setUpdateData}
            refreshFunc={getLogsByDate}
            userData={userData}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
